import React from 'react';
import {
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  styled,
  alpha,
  MenuProps,
  Divider,
  CircularProgress,
} from '@mui/material';
import { MoreHoriz as MenuIcon, ArrowDropDown } from '@mui/icons-material';

/**
 * ActionMenu is a versatile dropdown menu component that can be triggered by either a button with text
 * or an icon button. It supports both internal navigation (via onClick) and external links (via href).
 *
 * @component
 * @example
 * ```tsx
 * // Basic usage with text button
 * <ActionMenu
 *   id="my-menu"
 *   btnText="Actions"
 *   items={[
 *     { key: 'edit', label: 'Edit', onClick: () => console.log('edit') },
 *     { key: 'delete', label: 'Delete', onClick: () => console.log('delete') }
 *   ]}
 * />
 *
 * // Usage with icon button
 * <ActionMenu
 *   id="my-menu"
 *   items={[
 *     { key: 'edit', label: 'Edit', onClick: () => console.log('edit') },
 *     { key: 'delete', label: 'Delete', onClick: () => console.log('delete') }
 *   ]}
 * />
 * ```
 */

/**
 * Props for individual menu items
 * @interface ActionMenuItemProps
 * @property {string} key - Unique identifier for the menu item
 * @property {string} label - Display text for the menu item
 * @property {string} [href] - Optional URL for external links
 * @property {React.ReactNode} [icon] - Optional icon to display alongside the label
 * @property {() => void} [onClick] - Optional click handler for the menu item
 * @property {boolean} [disabled] - Optional flag to disable the menu item
 * @property {any} [btnProps] - Optional additional props to pass to the underlying button/link
 */

/**
 * Props for the ActionMenu component
 * @interface ActionMenuProps
 * @property {string} id - Unique identifier for the menu component
 * @property {string} [btnText] - Optional text to display on the trigger button. If not provided, an icon button is used
 * @property {any} [btnProps] - Optional additional props to pass to the trigger button
 * @property {ActionMenuItemProps[]} items - Array of menu items to display
 */

export type ActionMenuItemProps = {
  href?: string;
  key: string;
  icon?: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  btnProps?: any;
  loading?: boolean;
  divider?: boolean;
};
export type ActionMenuProps = {
  id: string;
  btnText?: string;
  btnProps?: any;
  items: ActionMenuItemProps[];
  anchorOrigin?: 'left' | 'right';
};

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: 'rgb(55, 65, 81)',
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      color: theme.palette.text.secondary,
      fontWeight: 'normal',
      width: '100%',
      justifyContent: 'space-between',
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(1),
      },
      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

const ActionMenu: React.FC<ActionMenuProps> = ({
  id,
  btnText,
  items = [],
  btnProps,
  anchorOrigin = 'right',
}: ActionMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {btnText ? (
        <Button
          id={id}
          data-testid='action-menu-trigger'
          variant='contained'
          onClick={handleClick}
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          endIcon={<ArrowDropDown />}
          {...(() => {
            const { loading, ...restProps } = btnProps || {};
            return restProps;
          })()}
        >
          {btnText}{' '}
          {btnProps?.loading && (
            <CircularProgress
              sx={{ ml: 1 }}
              size={16}
            />
          )}
        </Button>
      ) : (
        <IconButton
          id={id}
          data-testid='action-menu-trigger'
          onClick={handleClick}
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
        >
          <MenuIcon />
        </IconButton>
      )}
      <StyledMenu
        id='action-menu'
        data-testid='action-menu-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: anchorOrigin,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: anchorOrigin,
        }}
        MenuListProps={{
          'aria-labelledby': 'action-menu-button',
        }}
      >
        {items.map((item) =>
          item.divider ? (
            <Divider key={item.key} />
          ) : item.href ? (
            <Link
              key={item.key}
              data-testid={`${id}-${item.key}`}
              href={item.href}
              sx={{ display: 'flex' }}
              {...item.btnProps}
            >
              <MenuItem
                onClick={(e) => {
                  handleClose();
                  if (item.onClick) {
                    e.preventDefault();
                    item.onClick();
                  }
                }}
                disabled={item.disabled}
                sx={{ width: '100%' }}
                dense
              >
                <span>{item.label}</span>
                {item.icon}
              </MenuItem>
            </Link>
          ) : (
            <MenuItem
              key={item.key}
              onClick={(e) => {
                handleClose();
                if (item.onClick) {
                  e.preventDefault();
                  item.onClick();
                }
              }}
              disabled={item.disabled}
              sx={{ width: '100%' }}
            >
              <span>{item.label}</span>
              {item.icon}
              {item.loading && (
                <CircularProgress
                  sx={{ ml: 1 }}
                  size={16}
                />
              )}
            </MenuItem>
          )
        )}
      </StyledMenu>
    </>
  );
};

export default ActionMenu;
